import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
    ActionFlowRequestDTO,
    RequestComment,
    RequestSearchParams
} from '../types/request';
import type { ApprovalConfig } from '../types/approval';
import {DataRenderConfig} from "@/modules/action-flow/config/types/dynamic-form-types";

interface RequestState {
    // Requests data
    pendingRequests: ActionFlowRequestDTO[];
    activeRequest: ActionFlowRequestDTO | null;
    requestComments: RequestComment[];

    // Form and configuration
    dynamicFormConfig: DataRenderConfig | null;
    approvalConfig: ApprovalConfig;

    // UI state
    searchParams: RequestSearchParams;
    isLoading: boolean;
    error: Error | null;
}

interface RequestActions {
    // Request actions
    setPendingRequests: (requests: ActionFlowRequestDTO[]) => void;
    setActiveRequest: (request: ActionFlowRequestDTO | null) => void;
    setRequestComments: (comments: RequestComment[]) => void;

    // Configuration actions
    setDynamicFormConfig: (config: DataRenderConfig | null) => void;
    setApprovalConfig: (config: ApprovalConfig) => void;

    // UI actions
    setSearchParams: (params: Partial<RequestSearchParams>) => void;
    setLoading: (isLoading: boolean) => void;
    setError: (error: Error | null) => void;
    reset: () => void;
}

const initialApprovalConfig: ApprovalConfig = {
    canApprove: true,
    canReject: true,
    canReturn: false,
    requireComment: true,
    minCommentLength: 10,
    approverCount: 1,
    autoSubmitOnApproval: false
};

const initialSearchParams: RequestSearchParams = {
    q: '',
    domain: undefined,
    status: 'PENDING',
    sortBy: 'scopeDate',
    sortOrder: 'desc',
    limit: 20,
    page: 1
};

const initialState: RequestState = {
    pendingRequests: [],
    activeRequest: null,
    requestComments: [],
    dynamicFormConfig: null,
    approvalConfig: initialApprovalConfig,
    searchParams: initialSearchParams,
    isLoading: false,
    error: null
};

type RequestStore = RequestState & RequestActions;

export const useRequestStore = create<RequestStore>()(
    devtools(
        (set) => ({
            ...initialState,

            // Request actions
            setPendingRequests: (requests) => set({ pendingRequests: requests }),

            setActiveRequest: (request) => set({
                activeRequest: request,
                // Reset comments when changing active request
                requestComments: initialState.requestComments
            }),

            setRequestComments: (comments) => set({ requestComments: comments }),

            // Configuration actions
            setDynamicFormConfig: (config) => set({ dynamicFormConfig: config }),

            setApprovalConfig: (config) => set({
                approvalConfig: {
                    ...initialApprovalConfig,
                    ...config
                }
            }),

            // UI actions
            setSearchParams: (params) => set(state => ({
                searchParams: { ...state.searchParams, ...params }
            })),

            setLoading: (isLoading) => set({ isLoading }),

            setError: (error) => set({ error }),

            reset: () => set(initialState)
        }),
        { name: 'RequestStore' }
    )
);

